import { useEffect, useState } from 'react';

/**
 * Salon: masaların canlı doluluk listesi.
 *
 * Colyseus'un hazır `LobbyRoom`'una bağlanıp oda listesini `+`/`-` delta
 * mesajlarıyla takip ediyordu. Orada aynı masa koduna birden fazla oda
 * düşebiliyordu (maç başlayınca oda kilitlenip yenisi açılıyordu) ve istemci
 * hangisinin masayı temsil ettiğini `rank` ile ayıklamak zorundaydı.
 *
 * Durable Object'te masa başına TEK örnek var: `getByName(kod)` her zaman
 * aynı masayı veriyor. Bu yüzden o ayıklama mantığının tamamı silindi —
 * salon artık masa kodundan durumuna düz bir eşleme.
 */

const SERVER_URL = import.meta.env.VITE_SERVER_URL ?? 'ws://localhost:8787';

/** Salondaki masalar. Sayı değişirse tek yer burası. */
export const TABLE_CODES = ['MASA1', 'MASA2'] as const;

export interface TableInfo {
  code: string;
  /** Masadaki oyuncu adları — boş koltuklar listede yok. */
  players: string[];
  /** Maç başladı mı? Başladıysa masa kapalı. */
  playing: boolean;
}

/** Sunucudan gelen ham liste: masa kodu → durum. */
type Payload = Record<string, { players?: string[]; status?: string }>;

const emptyTables = (): TableInfo[] =>
  TABLE_CODES.map((code) => ({ code, players: [], playing: false }));

/**
 * Masaların canlı durumu. Sunucuya ulaşılamazsa boş masalar dönüyor —
 * salon ekranı yine çiziliyor, oturmaya çalışınca hata veriyor.
 */
export function useLobby(active: boolean): { tables: TableInfo[]; online: boolean } {
  const [tables, setTables] = useState<TableInfo[]>(emptyTables);
  const [online, setOnline] = useState(false);

  useEffect(() => {
    if (!active) return;
    let disposed = false;

    let socket: WebSocket;
    try {
      socket = new WebSocket(`${SERVER_URL.replace(/\/+$/, '')}/lobby`);
    } catch {
      setOnline(false);
      return;
    }

    socket.addEventListener('open', () => {
      if (!disposed) setOnline(true);
    });

    socket.addEventListener('message', (event) => {
      if (disposed || typeof event.data !== 'string') return;
      let envelope: { t?: string; d?: unknown };
      try {
        envelope = JSON.parse(event.data) as { t?: string; d?: unknown };
      } catch {
        return;
      }
      if (envelope.t !== 'tables') return;

      const payload = (envelope.d ?? {}) as Payload;
      setTables(
        TABLE_CODES.map((code) => {
          const found = payload[code];
          return {
            code,
            players: found?.players ?? [],
            playing: found?.status === 'playing',
          };
        }),
      );
    });

    const offline = () => {
      if (!disposed) setOnline(false);
    };
    socket.addEventListener('close', offline);
    socket.addEventListener('error', offline);

    return () => {
      disposed = true;
      socket.close(1000, 'salon kapandı');
    };
  }, [active]);

  return { tables, online };
}
